import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useMediaQuery } from '@mui/material';
import { debounce } from '@mui/material/utils';
import React, { useEffect, useMemo, useState } from 'react';
import { Box } from 'src/components/Box';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { Typography } from 'src/components/Typography';
import { Theme } from 'src/components/muiTheme/types';
import { CartItem as CartItemType } from 'src/models/cart/_types';
import {
  FabContent,
  ItemBrowser,
  Section,
  SectionedItemSelector,
  SidebarDialogLayout,
} from 'src/shared/SidebarDialog';
import { selectableItemTypes } from 'src/shared/constants/itemTypes';
import { Identifier } from 'src/types/utilityTypes';

import { PosConfigItemBrowser } from './PosConfigItemBrowser';

// Type for POS selected items (not CartItems)
interface PosSelectedItem {
  id: Identifier;
  name: string;
  type: string;
  itemId: Identifier;
  itemType: string;
  description?: string;
  paymentOptions?: any[];
  quantity: number;
}

interface PosConfigItemSelectorDialogProps {
  open: boolean;
  type: 'pos' | 'kiosk';
  onClose: () => void;
  onSave: (items: PosSelectedItem[]) => void;
}

export const PosConfigItemSelectorDialog: React.FC<
  PosConfigItemSelectorDialogProps
> = ({ open, onClose, onSave, type }) => {
  const [selectedItemType, setSelectedItemType] = useState<string | null>(null);
  const [selectedSection, setSelectedSection] = useState<string | null>(null);
  const [posItems, setPosItems] = useState<PosSelectedItem[]>([]);
  const [itemSearchValue, setItemSearchValue] = useState('');
  const [debouncedItemSearchValue, setDebouncedItemSearchValue] = useState('');

  // Create the debounced function using the same pattern as PaymentDialogContext
  const debouncedSetItemSearch = useMemo(
    () => debounce((value: string) => setDebouncedItemSearchValue(value), 300),
    []
  );

  useEffect(() => {
    debouncedSetItemSearch(itemSearchValue);
    return () => {
      debouncedSetItemSearch.clear();
    };
  }, [itemSearchValue, debouncedSetItemSearch]);

  const handleItemClick = (itemType: string, section: string) => {
    setSelectedItemType(itemType);
    setSelectedSection(section);
    // Reset item search when switching types
    setItemSearchValue('');
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleSelectItem = (item: any, options?: any): Promise<void> => {
    // Add item to pos items - spread the item and add quantity
    const posItem = {
      ...item,
      quantity: 1, // Default quantity
    } as PosSelectedItem;
    setPosItems((prev) => [...prev, posItem]);

    return Promise.resolve();
  };

  const handleRemoveItem = (item: CartItemType, index?: number) => {
    // Remove item from pos items
    if (index !== undefined) {
      setPosItems((prev) => prev.filter((_, i) => i !== index));
    } else {
      setPosItems((prev) => prev.filter((posItem) => posItem.id !== item.id));
    }
  };

  const handleSaveItems = () => {
    onSave(posItems);
    setPosItems([]); // Clear selected items after saving
    onClose();
  };

  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const selectItemTypeHeader = selectedItemType
    ? selectableItemTypes.find(
        (itemType) => itemType.value === selectedItemType
      )?.label
    : 'POS Item Selector';
  const showSidebar = !mdDown;
  const isKiosk = type === 'kiosk';

  const sections: Section[] = [
    {
      title: 'Library',
      items: isKiosk
        ? [
            {
              label: 'Products',
              icon: icon({ name: 'tag', style: 'regular' }),
              iconColor: 'primary' as const,
              selected:
                selectedItemType === 'product' && selectedSection === 'Library',
              onClick: () => handleItemClick('product', 'Library'),
            },
          ]
        : [
            {
              label: 'Packages',
              icon: icon({ name: 'box', style: 'regular' }),
              iconColor: 'primary' as const,
              selected:
                selectedItemType === 'package' && selectedSection === 'Library',
              onClick: () => handleItemClick('package', 'Library'),
            },
            {
              label: 'Groups',
              icon: icon({ name: 'users', style: 'regular' }),
              iconColor: 'primary' as const,
              selected:
                selectedItemType === 'group' && selectedSection === 'Library',
              onClick: () => handleItemClick('group', 'Library'),
            },
            {
              label: 'Plans',
              icon: icon({ name: 'list', style: 'regular' }),
              iconColor: 'primary' as const,
              selected:
                selectedItemType === 'plan' && selectedSection === 'Library',
              onClick: () => handleItemClick('plan', 'Library'),
            },
            {
              label: 'Products',
              icon: icon({ name: 'tag', style: 'regular' }),
              iconColor: 'primary' as const,
              selected:
                selectedItemType === 'product' && selectedSection === 'Library',
              onClick: () => handleItemClick('product', 'Library'),
            },
          ],
    },
    {
      title: 'Finders',
      items: isKiosk
        ? [
            {
              label: 'Products',
              icon: icon({ name: 'tag', style: 'regular' }),
              iconColor: 'primary' as const,
              selected:
                selectedItemType === 'product' && selectedSection === 'Finders',
              onClick: () => handleItemClick('product', 'Finders'),
            },
          ]
        : [
            {
              label: 'Packages',
              icon: icon({ name: 'box', style: 'regular' }),
              iconColor: 'primary' as const,
              selected:
                selectedItemType === 'package' && selectedSection === 'Finders',
              onClick: () => handleItemClick('package', 'Finders'),
            },
            {
              label: 'Groups',
              icon: icon({ name: 'users', style: 'regular' }),
              iconColor: 'primary' as const,
              selected:
                selectedItemType === 'group' && selectedSection === 'Finders',
              onClick: () => handleItemClick('group', 'Finders'),
            },
            {
              label: 'Plans',
              icon: icon({ name: 'list', style: 'regular' }),
              iconColor: 'primary' as const,
              selected:
                selectedItemType === 'plan' && selectedSection === 'Finders',
              onClick: () => handleItemClick('plan', 'Finders'),
            },
            {
              label: 'Products',
              icon: icon({ name: 'tag', style: 'regular' }),
              iconColor: 'primary' as const,
              selected:
                selectedItemType === 'product' && selectedSection === 'Finders',
              onClick: () => handleItemClick('product', 'Finders'),
            },
          ],
    },
  ];

  const sidebarContent = (
    <SectionedItemSelector
      searchValue={itemSearchValue}
      onSearchChange={selectedItemType ? setItemSearchValue : undefined}
      searchPlaceholder="Search items..."
      sections={sections}
      sx={{ mt: { sx: 1, md: 0 } }}
    />
  );

  const detailContent = selectedItemType ? (
    selectedSection === 'Library' ? (
      <ItemBrowser
        user={undefined}
        selectedItemType={selectedItemType}
        searchValue={itemSearchValue}
        onSearchChange={setItemSearchValue}
        debouncedSearchValue={debouncedItemSearchValue}
        onSelect={handleSelectItem}
        onRemove={handleRemoveItem}
        cartItems={posItems as CartItemType[]}
        cart={null}
        includeOnlineTraining={false}
        checkIfItemAdded={(item) => {
          return !!posItems.find((i) => {
            return i.id === item.id && i.type === item.type;
          });
        }}
      />
    ) : selectedSection === 'Finders' ? (
      <PosConfigItemBrowser
        selectedItemType={selectedItemType}
        searchValue={itemSearchValue}
        onSearchChange={setItemSearchValue}
        debouncedSearchValue={debouncedItemSearchValue}
        onSelect={handleSelectItem}
        onRemove={handleRemoveItem}
        cartItems={posItems as CartItemType[]}
      />
    ) : null
  ) : (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        {selectedSection === 'Finders'
          ? `Find ${selectedItemType || 'Items'}`
          : 'Select an item from the sidebar'}
      </Typography>
      <Typography variant="body2">
        {selectedSection === 'Library'
          ? 'Choose a category from the Library section to browse items.'
          : selectedSection === 'Finders'
          ? 'Select a category from the Finders section to browse items.'
          : 'Select an item from the sidebar to see details here.'}
      </Typography>
    </Box>
  );

  const mainContent =
    mdDown && !selectedItemType ? sidebarContent : detailContent;

  const fabContent = (
    <FabContent
      itemCount={posItems.length}
      user={undefined}
      showCheckIcon={true}
      checkIconVisible={true}
      text={`${posItems.length} item${
        posItems.length === 1 ? '' : 's'
      } selected`}
      showAvatar={false}
    />
  );

  return (
    <SidebarDialogLayout
      open={open}
      onClose={onClose}
      title={selectItemTypeHeader}
      leftAdornment={
        selectedItemType ? (
          <IconButton
            onClick={() => {
              setSelectedItemType(null);
              setSelectedSection(null);
              setItemSearchValue('');
            }}
            color="secondary"
          >
            <Icon icon={icon({ name: 'arrow-left', style: 'regular' })} />
          </IconButton>
        ) : undefined
      }
      maxWidth="md"
      showSidebar={showSidebar}
      showFab={true}
      fabActive={posItems.length > 0}
      fabContent={fabContent}
      fabOnClick={handleSaveItems}
      sidebarContent={sidebarContent}
      mainContent={mainContent}
    />
  );
};
